import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import StoreTable from '@/components/table/store/StoreTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { formatDate } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IStore } from '@/interfaces/api/IStore';
import { ICollectionStore } from '@/interfaces/api/store/ICollectionStore';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const Stores = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchQuery, setSearchQuery] = useState('');

	const filter = {
		'page[size]': pageSize,
		'page[number]': pageNumber,
		...(searchQuery && { 'filter[tradeName]': searchQuery }),
	};

	const {
		data: stores,
		isLoading: storeIsLoading,
		isError: storeIsError,
		error: storeError,
		refetch: storeRefetch,
	} = useQuery<ICollectionStore>({
		queryKey: [ApiQueryKeys.getAllStores, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllStores({
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	if (storeIsError) {
		if (storeError instanceof Error) {
			notificationService.error(storeError.message);
		} else {
			notificationService.error('Unknown error fetching stores');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionStore,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const columnHelper = createColumnHelper<IStore>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),
		columnHelper.accessor('users', {
			cell: (info) => {
				const users = info.getValue();
				const firstUser = users?.[0];
				const displayName = firstUser?.businessName;
				return (
					<div className="w-[200px] truncate" title={displayName}>
						{displayName}
					</div>
				);
			},
			header: () => <div className="w-[200px]">BUSINESS NAME</div>,
		}),
		columnHelper.accessor('users', {
			cell: (info) => {
				const users = info.getValue();
				const firstUser = users?.[0];
				const userEmail = firstUser?.username || 'N/A';
				return (
					<div className="w-[250px] truncate" title={userEmail}>
						{userEmail}
					</div>
				);
			},
			header: () => <div className="w-[250px]">USER EMAIL</div>,
		}),
		columnHelper.accessor('tradeName', {
			cell: (info) => (
				<div className="w-[150px] truncate" title={info.getValue()}>
					{info.getValue()}
				</div>
			),
			header: () => <div className="w-[150px]">TRADE NAME</div>,
		}),
		columnHelper.accessor('users', {
			cell: (info) => {
				const users = info.getValue();
				const firstUser = users?.[0];
				const inventoryType = firstUser?.inventoryType || 'N/A';
				return <div className="w-[120px]">{inventoryType}</div>;
			},
			header: () => <div className="w-[120px]">INVENTORY TYPE</div>,
		}),
		columnHelper.accessor('storeURL', {
			cell: (info) => {
				const store = info.row.original;
				const url = store.storeURL;
				const region = store.storeRegion;
				const email = store.contactEmail;

				return (
					<div className="w-[275px] space-y-1">
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium text-gray-600 w-12">
								URL:
							</span>
							{url ? (
								<a
									href={url}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:text-blue-800 underline text-sm truncate flex-1"
									title={url}
								>
									{url}
								</a>
							) : (
								<span className="text-gray-400 text-sm">N/A</span>
							)}
						</div>
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium text-gray-600 w-12">
								Region:
							</span>
							<span className="text-sm truncate flex-1" title={region}>
								{region || <span className="text-gray-400">N/A</span>}
							</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-sm font-medium text-gray-600 w-12">
								Email:
							</span>
							{email ? (
								<a
									href={`mailto:${email}`}
									className="text-blue-600 hover:text-blue-800 underline text-sm truncate flex-1"
									title={email}
								>
									{email}
								</a>
							) : (
								<span className="text-gray-400 text-sm">N/A</span>
							)}
						</div>
					</div>
				);
			},
			header: () => <div className="w-[275px]">SHOP DETAILS</div>,
		}),

		columnHelper.accessor('users', {
			cell: (info) => (
				<div className="w-[100px] text-right pr-[40px]">
					{info.getValue()?.length}
				</div>
			),
			header: () => <div className="w-[100px]">MERCHANTS</div>,
		}),
		columnHelper.accessor('haggleEvents', {
			cell: (info) => (
				<div className="w-[80px] text-right pr-[25px]">
					{info.getValue()?.length}
				</div>
			),
			header: () => <div className="w-[80px]">Haggles</div>,
		}),
		columnHelper.accessor('createdAt', {
			cell: (info) => (
				<div className="w-[80px]">{formatDate(info.getValue())}</div>
			),
			header: () => <div className="w-[80px]">DATE ADDED</div>,
		}),
	];

	return (
		<div className="min-h-content overflow-y-auto">
			{storeIsLoading && <LoadingSkeletonModalTable />}
			{stores && !storeIsLoading && (
				<div className="flex flex-col h-full grow gap-4">
					<StoreTable
						collectionStore={stores}
						paginationOption={handlePaginationOptions(stores)}
						columns={columns}
						refetch={storeRefetch}
						query={searchQuery}
						setQuery={setSearchQuery}
						stores={stores.itemCount}
					/>
				</div>
			)}
		</div>
	);
};

export default Stores;
